import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItem,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'
import MoreVert from '@mui/icons-material/MoreVert'
import Launch from '@mui/icons-material/Launch'
import HelpOutline from '@mui/icons-material/HelpOutlineOutlined'
import { subMinutes, addMinutes } from 'date-fns'
import { SessionHelpButton } from './Zoom/SessionHelpButton'
import { ChangeAppointmentTypeModal } from './ChangeAppointmentTypeModal'
import type { Appointment, AppointmentCancelation } from './PatientAppointmentsService'
import { logger } from '~/shared/lib/logger'

export const AppointmentCardMenu: React.FC<{
  appointment: Appointment | AppointmentCancelation
  patientId: number
  selectAppointmentForCancel: (appointment: Appointment | AppointmentCancelation) => void
}> = ({ appointment, patientId, selectAppointmentForCancel }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)

  // `appointment` is a union of two divergent BFF shapes (scheduled vs. cancelation).
  // Only the scheduled arm carries `provider_id` / `cancelable_status`; narrow on
  // `canceled_at` so those fields read type-safely (cancelation rows fall back).
  const scheduledAppt = 'canceled_at' in appointment ? undefined : appointment

  // A cancelation row is an event log entry. Its own `id` names the event, not the appointment.
  const appointmentId = 'canceled_at' in appointment ? appointment.appointment_id : appointment.id

  const [isChangingType, setIsChangingType] = useState(false)
  // The server refuses a change it cannot make; the menu only hides it once the visit has started.
  const canChangeType = scheduledAppt?.cancelable_status.can_cancel === true

  // Links to DataDog for member and clinician surrounding this visit, padded by 10 minutes
  const datadogLinks = useMemo(() => {
    try {
      const startTime = subMinutes(new Date(appointment.start), 10).getTime().toString()
      const endTime = addMinutes(new Date(appointment.start), appointment.duration_minutes + 10)
        .getTime()
        .toString()
      const datadogParams = new URLSearchParams()
      datadogParams.set(
        'query',
        `service:(lucian-crm OR Firefly-App OR members-portal) env:(prod OR production) @usr.id:(${patientId} OR ${
          scheduledAppt?.provider_id ?? 0
        })`,
      )
      datadogParams.set('from_ts', startTime)
      datadogParams.set('to_ts', endTime)
      datadogParams.set('live', Boolean(false).toString())
      const params = datadogParams.toString()
      return {
        ['Datadog Logs']: `https://app.datadoghq.com/logs?${params}`,
        ['Datadog RUM Sessions']: `https://app.datadoghq.com/rum/explorer?${params}`,
        ['Datadog Errors']: `https://app.datadoghq.com/rum/error-tracking?${params}`,
      }
    } catch (e) {
      logger.error('[Appointment] generate Datadog links', e)
      return null
    }
  }, [appointment, patientId, scheduledAppt])

  // eslint-disable-next-line no-console -- this debugging button exists to print the appointment
  const logAppointment = () => console.log('Appointment', appointment)

  return (
    <>
      <SessionHelpButton />
      <IconButton onClick={handleClick}>
        <MoreVert />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <ListItem>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Chip
                label={appointmentId ?? 'No appointment'}
                size="small"
                color="secondary"
                sx={{ mr: 5 }}
              />
              <Tooltip title="Log appointment to console" placement="top">
                <Button size="small" onClick={logAppointment}>
                  Log Appointment
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </ListItem>
        <MenuItem>
          {scheduledAppt?.cancelable_status.can_cancel ? (
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                selectAppointmentForCancel(appointment)
                handleClose()
              }}
            >
              <Typography sx={{ fontSize: '1.6rem' }}>Cancel appointment</Typography>
            </Button>
          ) : (
            <Tooltip title={scheduledAppt?.cancelable_status.reason || JSON.stringify(appointment)}>
              <span>
                <Button disabled size="small" color="inherit" endIcon={<HelpOutline />}>
                  Not cancelable
                </Button>
              </span>
            </Tooltip>
          )}
        </MenuItem>
        {canChangeType ? (
          <MenuItem>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                setIsChangingType(true)
                handleClose()
              }}
            >
              <Typography sx={{ fontSize: '1.6rem' }}>Change visit type</Typography>
            </Button>
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem>
          <Typography variant="overline">Developer Tools</Typography>
        </MenuItem>
        <MenuItem>
          <Button
            href="https://zoom.us/account/metrics/pastmeetings"
            target="_blank"
            rel="noreferrer"
            endIcon={<Launch />}
            size="small"
          >
            Zoom Dashboard
          </Button>
        </MenuItem>

        {datadogLinks
          ? Object.entries(datadogLinks).map(([key, href]) => (
              <MenuItem key={key}>
                <Button
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  endIcon={<Launch />}
                  size="small"
                >
                  {key}
                </Button>
              </MenuItem>
            ))
          : null}
      </Menu>
      {isChangingType && scheduledAppt ? (
        <ChangeAppointmentTypeModal
          appointment={scheduledAppt}
          patientId={patientId}
          onClose={() => setIsChangingType(false)}
        />
      ) : null}
    </>
  )
}
